import { BookOpen, ClipboardList, ShoppingBag } from 'lucide-react';
import { PRODUCT_IMAGES } from '../../../../../branding/assets.js';
import {
  IntegrationCard,
  ModeOptionList,
} from './AIConfigurationUI.tsx';
import type { ModeSelectOption } from './AIConfigurationUI.tsx';
import {
  BooleanToggle,
  ChoiceConsequences,
  Field,
  SectionCard,
  inputBase,
} from './FormPromptControls.tsx';

type ModoPedidos = 'whatsapp' | 'link';

type OrdersStepProps = {
  isAtendabotBrand: boolean;
  showCardapio: boolean;
  showRegras: boolean;
  form: {
    frases_ou_regras_especiais?: string;
  };
  errors: Record<string, string>;
  modoPedidos: ModoPedidos | null;
  modoPedidosDirty: boolean;
  pedidosModeOptions: ModeSelectOption[];
  setModoPedidos: (id: ModoPedidos) => void;
  cardapioLinkPreview: string;
  usaCardapioOneMenu: boolean;
  externalCardapioInputValue: string;
  oneMenuAutoLink: string;
  openRelatedProduct: (path: string) => void;
  updateField: (key: string, value: unknown) => void;
};

const OrdersStep = ({
  isAtendabotBrand,
  showCardapio,
  showRegras,
  form,
  errors,
  modoPedidos,
  modoPedidosDirty,
  pedidosModeOptions,
  setModoPedidos,
  cardapioLinkPreview,
  usaCardapioOneMenu,
  externalCardapioInputValue,
  oneMenuAutoLink,
  openRelatedProduct,
  updateField,
}: OrdersStepProps) => (
  <>
    {!isAtendabotBrand && (
      <SectionCard
        title="Como a IA trata os pedidos"
        icon={ShoppingBag}
        description="Define se a IA registra o pedido feito pelas mensagens do WhatsApp ou se apenas envia o link do cardápio."
        hint="Esta é a mesma escolha feita na etapa “O que a IA faz”. Se mudar aqui, muda lá também."
      >
        <div
          className={`max-w-3xl space-y-4${modoPedidosDirty ? ' ai-inline-draft-block' : ''}`}
          data-error-key="modo_pedidos"
        >
          {modoPedidosDirty && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
              Rascunho
            </span>
          )}
          <ModeOptionList
            name="Como a IA deve tratar os pedidos?"
            size="large"
            options={pedidosModeOptions}
            value={modoPedidos}
            onSelect={(id) => setModoPedidos(id as ModoPedidos)}
          />
          {errors.modo_pedidos && (
            <p className="text-sm font-semibold text-rose-600">{errors.modo_pedidos}</p>
          )}
          {modoPedidos !== null && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4" aria-live="polite">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Prévia da resposta</p>
              <p className="mt-2 text-sm text-slate-700">
                <strong>Cliente:</strong> “Quero uma pizza grande de calabresa.”
              </p>
              <p className="mt-1 text-sm text-slate-700">
                <strong>IA:</strong>{' '}
                {modoPedidos === 'whatsapp'
                  ? '“Anotado! 1 pizza grande de calabresa. Vai ser entrega ou retirada?” A IA registra o pedido completo na conversa.'
                  : `“Você pode montar seu pedido direto no nosso cardápio: ${cardapioLinkPreview}. Qualquer dúvida sobre os itens, me chama!” Nesse modo, a IA não registra pedidos na conversa.`}
              </p>
            </div>
          )}
        </div>
      </SectionCard>
    )}

    {!isAtendabotBrand && showCardapio && (
      <SectionCard
        title="Cardápio"
        icon={BookOpen}
        description={
          modoPedidos === 'whatsapp'
            ? 'É deste cardápio que a IA tira itens, preços e adicionais para montar os pedidos na conversa.'
            : 'É este o link que a IA envia quando o cliente quiser pedir. Use o OneMenu ou informe o link de outro sistema.'
        }
      >
        <div className="space-y-6">
          <p className="text-sm font-semibold text-slate-800">De onde vem o seu cardápio?</p>
          <IntegrationCard
            logo={PRODUCT_IMAGES.oneMenu}
            name="OneMenu"
            status={usaCardapioOneMenu ? 'connected' : 'disconnected'}
            description="A IA usa os produtos, preços e opções cadastrados no OneMenu para apresentar o cardápio e montar pedidos na conversa."
            actionLabel="Abrir OneMenu"
            onAction={() => openRelatedProduct('/cardapio')}
          />
          <div className="max-w-3xl">
            <Field
              label="Usar o cardápio cadastrado no OneMenu?"
              error={errors.cardapio_cadastrado}
              errorKey="cardapio_cadastrado"
            >
              <div className="space-y-3">
                <BooleanToggle
                  value={usaCardapioOneMenu}
                  onChange={(value) => updateField('usar_cardapio_onemenu', value)}
                />
                <ChoiceConsequences
                  yes="a IA consulta diretamente os produtos, preços e adicionais do OneMenu. O link é obtido automaticamente e nenhum outro link precisa ser cadastrado."
                  no="o OneMenu não será usado. Informe abaixo o link do cardápio que a IA deve enviar ao cliente."
                />
              </div>
            </Field>
          </div>
          {!usaCardapioOneMenu && (
            <Field
              label="Link do cardápio"
              helper="Use este campo somente quando o cardápio estiver em outro sistema, fora do OneMenu."
              error={errors.link_cardapio}
              errorKey="link_cardapio"
              tooltip="Coloque o link do cardápio que a IA deve enviar quando o OneMenu não estiver em uso."
            >
              <input
                className={inputBase}
                value={externalCardapioInputValue}
                onChange={(e) => updateField('link_cardapio', e.target.value)}
                placeholder="Ex.: https://meusistema.com/cardapio"
              />
            </Field>
          )}
          {usaCardapioOneMenu && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold text-slate-700">Cardápio que será usado automaticamente</p>
              <p className="mt-1 break-all text-xs text-slate-600">
                {oneMenuAutoLink || 'O link será gerado automaticamente pelo OneMenu quando o restaurante estiver publicado.'}
              </p>
            </div>
          )}
        </div>
      </SectionCard>
    )}

    {showRegras && (
      <SectionCard
        title="Regras dos pedidos"
        icon={ClipboardList}
        description="Regras específicas que a IA deve seguir ao conduzir pedidos (ex.: valor mínimo, restrições, exceções)."
      >
        <Field
          label="Regras especiais dos pedidos (opcional)"
          error={errors.frases_ou_regras_especiais}
          errorKey="frases_ou_regras_especiais"
          helper="Use para regras que a IA deve seguir ao pé da letra ao registrar pedidos."
          tooltip="A IA segue essas regras ao pé da letra."
        >
          <textarea
            className={`${inputBase} min-h-[120px]`}
            value={form.frases_ou_regras_especiais}
            onChange={(e) => updateField('frases_ou_regras_especiais', e.target.value)}
            placeholder="Ex.: Pedido mínimo de R$ 30. Não aceitar meio a meio em promoções."
          />
        </Field>
      </SectionCard>
    )}
  </>
);

export default OrdersStep;
